import _ from 'lodash';
import { PlateProvider } from '@udecode/plate';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import ActionBar from 'components/text_editor_new/components/action_bar/action_bar';
import Button, { ButtonTypes } from 'components/common/button';
import { BoldItalicUnderlineControls, ListControls, Separator } from 'components/composer/shared/editor_controls_new';
import { Emoji } from 'components/composer/shared/editor_action_buttons_new';
import { TextEditorWrapper } from 'components/customer/task_comments/task_comment_editor_new_styles';
import EditorWrapper from 'components/customer/task_comments/task_comment_editor_wrapper';
import DatetimePickerMenu from 'components/lib/datetime_picker_menu';
import FontColorControls from 'components/composer/shared/font_color_controls';
import { getDatetimeFromTimestamp, getTimestampFromDatetime } from 'components/lib/date/datetime_helpers';
import { LinkButton } from 'components/composer/inline_links/link_button';
import { LinkEditor } from 'components/composer/inline_links/link_editor';
import nextDay from 'scripts/lib/next_day';
import TableMenu from 'components/composer/shared/editor_table_controls_new';
import TextAlignmentMenu from 'components/composer/shared/editor_alignment_controls_new';
import TextEditor from 'components/text_editor_new';
import useTaskEmailEditor from 'components/customer/task_comments/use_task_email_editor';

export default function TaskEmailEditor({ taskId, from, to, cc, defaultSubject, onCancel }) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState(defaultSubject || '');
  const [followUp, setFollowUp] = useState(() => getDatetimeFromTimestamp(nextDay()));
  const [sendErrors, setSendErrors] = useState([]);
  const dueAt = followUp ? getTimestampFromDatetime(followUp) : undefined;

  const sendContextRef = useRef({ taskId, from, to, cc, subject, dueAt });
  useEffect(() => {
    sendContextRef.current = { taskId, from, to, cc, subject, dueAt };
  }, [taskId, from, to, cc, subject, dueAt]);

  const editorProps = {
    initialHtml: '',
    placeholder: t('Type your reply…'),
    editorId: 'task-email-reply',
    attr: 'bodyHtml',
    name: 'bodyHtml',
    onChange: _.noop,
    onBlur: _.noop,
  };

  const [editorRef, onSubmit, isSending] = useTaskEmailEditor(editorProps, sendContextRef, setSendErrors);
  const editorNodeRef = useRef(null);

  return (
    <EditorWrapper data-aid="taskEmailEditor" editor={editorRef.current} id={editorProps.editorId} notSticky>
      <StyledRecipients data-aid="taskEmailEditor-recipients">
        <div>
          <StyledRecipientsLabel>{t('To:')}</StyledRecipientsLabel> {(to || []).join(', ')}
        </div>
        {cc && cc.length > 0 && (
          <div>
            <StyledRecipientsLabel>{t('Cc:')}</StyledRecipientsLabel> {cc.join(', ')}
          </div>
        )}
      </StyledRecipients>
      <StyledSubject
        data-aid="taskEmailEditor-subject"
        onChange={evt => setSubject(evt.target.value)}
        placeholder={t('Subject')}
        value={subject}
      />
      <PlateProvider editor={editorRef.current} id={editorProps.editorId}>
        <StyledActionBar data-aid="taskEmailEditor-formattingBar" role="toolbar">
          <BoldItalicUnderlineControls {...editorProps} />
          <FontColorControls {...editorProps} />
          <TextAlignmentMenu {...editorProps} />
          <ListControls {...editorProps} />
          <TableMenu {...editorProps} />
          <>
            <Separator />
            <LinkButton />
          </>
          <Emoji {...editorProps} />
        </StyledActionBar>
        <StyledEditorArea ref={editorNodeRef}>
          <TextEditor {...editorProps} minHeight="4.5rem" />
          <LinkEditor hotKey="mod+k" />
        </StyledEditorArea>
      </PlateProvider>
      <StyledFollowUp>
        <StyledFollowUpLabel>{t('Follow up by')}</StyledFollowUpLabel>
        <DatetimePickerMenu
          boundByWindow
          onChange={attrs => attrs.date && setFollowUp(attrs)}
          placeholderText={t('Follow-up date')}
          value={followUp}
        />
      </StyledFollowUp>
      {sendErrors.length > 0 && (
        <StyledSendErrors data-aid="taskEmailEditor-errors" role="alert">
          {sendErrors.map((error, index) => (
            <div key={index}>{error.detail}</div>
          ))}
        </StyledSendErrors>
      )}
      <Footer>
        {onCancel && (
          <Button buttonType={ButtonTypes.TEXT} data-aid="taskEmailEditor-cancel" onClick={onCancel}>
            {t('Cancel')}
          </Button>
        )}
        <Button
          buttonType={ButtonTypes.PRIMARY}
          data-aid="taskEmailEditor-send"
          disabled={isSending}
          onClick={onSubmit}
        >
          {t('Send')}
        </Button>
      </Footer>
    </EditorWrapper>
  );
}

TaskEmailEditor.propTypes = {
  taskId: PropTypes.string.isRequired,
  from: PropTypes.string,
  to: PropTypes.arrayOf(PropTypes.string),
  cc: PropTypes.arrayOf(PropTypes.string),
  defaultSubject: PropTypes.string,
  onCancel: PropTypes.func,
};

// The composer's bar, minus its drop shadow: in the panel it sits flush on the editor, not floating
// over a conversation. ActionBar only forwards className, so the test id lives on a wrapper.
function StyledActionBar({ children, ...props }) {
  return (
    <StyledActionBarWrapper {...props}>
      <ActionBar>{children}</ActionBar>
    </StyledActionBarWrapper>
  );
}

StyledActionBar.propTypes = {
  children: PropTypes.node,
};

const StyledActionBarWrapper = styled.div`
  display: flex;
  margin-bottom: 8px;

  > div > span {
    border: none;
    box-shadow: none;
    padding-left: 0;
  }
`;

// TextEditorWrapper and TextEditor both set the editable's min-height with !important at equal
// specificity, so the minimum needs a more specific selector (&&) to win.
const StyledEditorArea = styled(TextEditorWrapper)`
  && [data-slate-editor='true'] {
    max-height: 200px;
    min-height: 4.5rem !important;
    overflow-y: auto;
  }
`;

const StyledRecipients = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: 12px;
  margin-bottom: 8px;
  word-break: break-word;
`;

const StyledRecipientsLabel = styled.span`
  font-weight: 600;
`;

const StyledSendErrors = styled.div`
  color: ${p => p.theme.colors.red400};
  font-size: 12px;
  margin-top: 8px;
`;

const StyledSubject = styled.input`
  border: 1px solid ${p => p.theme.colors.gray400};
  border-radius: 6px;
  margin-bottom: 8px;
  outline: none;
  padding: 8px 12px;
  width: 100%;

  &:focus {
    border-color: ${p => p.theme.colors.green400};
  }
`;

const Footer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const StyledFollowUp = styled.div`
  align-items: center;
  display: flex;
  margin-top: 8px;
`;

const StyledFollowUpLabel = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: 12px;
  padding-right: 6px;
`;
